import { isSignal } from '@angular/core';
import { uuid } from '@softline/core';
import { Filter } from '../../types/filter';
import { FilterItemConfig } from '../../types/filter-config';
import { FilterOperator } from '../../types/filter-operators';
import { isValuelessOperator } from '../../helper/builder-operators.function';
import { PageFilterItem, PageFilterToggleValue } from '../types/page-filter-item';

/**
 * Builds the filter node a toggle item writes when switched on. Signal values
 * are read here, so dynamic values (e.g. rolling dates) resolve at toggle time.
 */
export function createToggleFilter(item: PageFilterItem<any, any>): Filter<any> {
  const toggle = item.toggle;
  if (!toggle)
    throw new Error(`[createToggleFilter] Item for field ${item.field} is not a toggle item`);
  const value = isSignal(toggle.value) ? toggle.value() : toggle.value;
  validateToggleValue(item.config, item.operator, value, 'createToggleFilter');
  const filter: Record<string, unknown> = {
    uuid: uuid(),
    type: item.config.type,
    field: item.field,
    operator: item.operator,
  };
  if (value !== undefined) filter['value'] = value;
  return filter as unknown as Filter<any>;
}

/**
 * Validates a toggle value against the field's config type and the operator:
 * operator-only filters take no value, in/notIn take arrays, between takes a
 * range, everything else a scalar of the field's datatype.
 */
export function validateToggleValue<TValue>(
  config: FilterItemConfig<any>,
  operator: FilterOperator,
  value: PageFilterToggleValue<TValue> | undefined,
  context: string
): void {
  const field = config.field;
  if (isValuelessOperator(operator)) {
    if (value !== undefined)
      throw new Error(
        `[${context}] Operator ${operator} of field ${field} is operator-only and must not have a value`
      );
    return;
  }
  if (value === undefined || value === null)
    throw new Error(`[${context}] Operator ${operator} of field ${field} requires a value`);

  if (operator === 'in' || operator === 'notIn') {
    if (!Array.isArray(value))
      throw new Error(`[${context}] Operator ${operator} of field ${field} requires an array value`);
    if (value.length === 0)
      throw new Error(`[${context}] Operator ${operator} of field ${field} requires a non-empty array value`);
    for (const element of value) assertDatatype(config, element, context);
    return;
  }
  if (operator === 'between') {
    if (typeof value !== 'object' || Array.isArray(value) || !('from' in value) || !('to' in value))
      throw new Error(`[${context}] Operator between of field ${field} requires a { from, to } range value`);
    assertDatatype(config, (value as { from: unknown }).from, context);
    assertDatatype(config, (value as { to: unknown }).to, context);
    return;
  }
  if (Array.isArray(value))
    throw new Error(`[${context}] Operator ${operator} of field ${field} requires a single value, not an array`);
  assertDatatype(config, value, context);
}

function assertDatatype(config: FilterItemConfig<any>, value: unknown, context: string): void {
  const field = config.field;
  switch (config.type) {
    case 'string':
    case 'date':
    case 'dateTime':
    case 'dateTimeTz':
      if (typeof value !== 'string')
        throw new Error(`[${context}] Field ${field} (${config.type}) requires string values, got ${typeof value}`);
      return;
    case 'number':
      if (typeof value !== 'number')
        throw new Error(`[${context}] Field ${field} (number) requires number values, got ${typeof value}`);
      return;
    case 'entity':
      if (typeof value !== 'object' || value === null)
        throw new Error(`[${context}] Field ${field} (entity) requires entity object values, got ${typeof value}`);
      return;
    case 'boolean':
      throw new Error(`[${context}] Field ${field} (boolean) is operator-only and cannot carry a value`);
    default:
      // custom types define their own value shape - no static check possible
      return;
  }
}
